import React, {Fragment} from 'react';

import cs from 'classnames';
import moment, {Moment} from 'moment-tz';
import {Link} from 'react-router';

import {MultipleAssignedAvatar} from 'app/components/general/avatars/MultipleAssignedAvatar';
import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {DocumentSequencedStepsStatusIcon} from 'app/general/doc-status';
import {CheckboxButton} from 'app/general/widgets/CheckboxButton';
import {DocumentIconWithNotifications} from 'app/notifly/components/DocumentIconWithNotifications';
import {DashboardItemPresentMembers} from 'app/presence/components/DashboardItemPresentMembers';
import {PercentageBadge} from 'app/tasks/components/TaskPercentageBadge';
import {useTaskUpdate} from 'app/tasks/task-update-hook';
import {isOverdue} from 'app/tasks/utils';
import {cannotHoverAndCanTouch} from 'app/utils/browser';
import {gt} from 'app/utils/i18n';
import {deepDiffer} from 'app/utils/react-utils';

import {useBulkTasksSelect} from './BulkSelectTasksContext';
import {CompactTaskItemActionsButton} from './CompactTaskItemActionsButton';
import {
  StyledBigCheckboxDiv,
  StyledTaskItemComponent,
} from './StyledComponents';

import type {NotificationType} from 'app/notifly/types';
import type {SlimTaskInstanceSerializedType} from 'sweet-types';

interface Props {
  task: SlimTaskInstanceSerializedType;
  userTimezone: string;
  showNowLine?: boolean;
  notifications?: Array<NotificationType>;
}

export const CompactTaskItem = React.memo(
  ({task, userTimezone, showNowLine, notifications}: Props): JSX.Element => {
    const {toggleTaskCompleted} = useTaskUpdate();
    const {
      enableBulkSelect,
      isTaskSelected,
      addToSelection,
      removeFromSelection,
    } = useBulkTasksSelect();

    const localise = (date?: string | Date) => {
      return moment(date).tz(userTimezone);
    };

    const localisedDue = localise(task.due);
    const taskIsOverdue = isOverdue(task);
    const assigned_object = task.template.assigned_object;
    const [title, subTitle] = React.useMemo(() => getTitles(task), [task]);

    const isPast = React.useMemo(() => {
      const now = localise();
      const due = localise(task.due);
      return now.isAfter(due);
    }, [task.due]);

    const areTooltipsEnabled = React.useMemo(() => {
      const usingIE = /Trident\/[67]/.test(window.navigator.userAgent);
      return (
        !usingIE &&
        !cannotHoverAndCanTouch &&
        task.description != null &&
        task.description !== ''
      );
    }, []);

    function addTooltip(content: JSX.Element, subTitle: undefined | string) {
      return areTooltipsEnabled ? (
        <LazyTooltip hide={subTitle == null} tooltip={subTitle}>
          {content}
        </LazyTooltip>
      ) : (
        content
      );
    }

    const assignees = React.useMemo(
      () => [
        ...task.template.users,
        ...task.template.teams,
        ...task.template.roles,
      ],
      [task.template.users, task.template.teams, task.template.roles]
    );

    return (
      <StyledTaskItemComponent
        task={task}
        isOverdue={taskIsOverdue}
        showNowLine={showNowLine}
        isPast={isPast}
        PresenceNode={
          <DashboardItemPresentMembers
            contentType="taskinstance"
            objectId={task.id}
          />
        }
        TimeNode={
          <Link
            to={task.html_url}
            className={cs('ml-auto ml-sm-0', {'text-danger': taskIsOverdue})}
          >
            {formatTime(localisedDue)}
          </Link>
        }
        BulkSelectNode={
          enableBulkSelect ? (
            <CheckboxButton
              isChecked={isTaskSelected(task)}
              onClick={(isChecked) =>
                isChecked ? addToSelection(task) : removeFromSelection(task)
              }
              useRealCheckbox={true}
              className="mr-3 btn btn-sm btn-link"
            />
          ) : null
        }
        AssignedNode={
          <MultipleAssignedAvatar
            className="ml-auto ml-sm-0"
            usersAndTeamsAndRoles={assignees}
            tooltipTitle={gt('Assigned to')}
          />
        }
        StatsNode={
          <Fragment>
            {task.comment_count != null && task.comment_count > 0 ? (
              <Link className="badge badge-light mr-1" to={task.comments_url}>
                <i className="far fa-comments" /> {task.comment_count}
              </Link>
            ) : null}
            <PercentageBadge
              task={task}
              isOverdue={taskIsOverdue}
              size="3rem"
            />
          </Fragment>
        }
        ItemNameNode={addTooltip(
          <div className="text-truncate">
            <Link
              to={task.html_url}
              className="text-dark font-weight-bold d-block d-sm-inline"
            >
              <DocumentIconWithNotifications
                contentType={assigned_object.content_type}
                notifications={notifications}
                className="text-secondary font-weight-light text-secondary"
              />{' '}
              {title}
            </Link>
            {subTitle !== null && (
              <span className="pl-0 pl-sm-2 text-muted font-weight-light font-italic">
                {subTitle}
              </span>
            )}
          </div>,
          subTitle
        )}
        ActionsNode={
          <div className="d-flex align-items-start align-items-sm-center flex-row flex-column-reverse flex-sm-row">
            <CompactTaskItemActionsButton task={task} />
            {addTooltip(
              <StyledBigCheckboxDiv>
                {!task.is_completeable ? (
                  <DocumentSequencedStepsStatusIcon
                    isSequenced={
                      task.template.assigned_object.steps_blocked_at != null
                    }
                    className="btn px-1"
                    contentType={task.template.assigned_object.content_type}
                  />
                ) : (
                  <CheckboxButton
                    fixedWidth={true}
                    disabled={false}
                    onClick={() => toggleTaskCompleted(task)}
                    isChecked={task.completed != null}
                    className={cs('btn btn-link btn-lg py-0 px-1', {
                      'text-danger': taskIsOverdue,
                    })}
                  />
                )}
              </StyledBigCheckboxDiv>,

              task.is_completeable
                ? gt('Click to mark the task as complete')
                : undefined
            )}
          </div>
        }
      />
    );
  },
  (props, nextProps) => {
    const {task, userTimezone, showNowLine, notifications} = props;
    return (
      showNowLine === nextProps.showNowLine &&
      userTimezone === nextProps.userTimezone &&
      !deepDiffer(notifications, nextProps.notifications) &&
      !deepDiffer(task, nextProps.task)
    );
  }
);

const formatTime = (date: Moment) => date.format('h:mm A');

function getTitles(
  task: SlimTaskInstanceSerializedType
): [string, string | undefined] {
  let titles: Array<string> = [];

  if (task.description != null) {
    titles.push(task.description);
  }
  titles.push(task.name);
  return titles as [string, string | undefined];
}
